import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Bridge between page content and the app shell header: a page (today,
 * ReportPageShell) registers contextual actions while mounted, and the
 * header (Layout/shell/AppHeader) renders them beside its built-in icons.
 * Actions are plain data — { key, label, onClick, disabled?, spinning?,
 * primary? } — the header maps `key` to an icon, so nothing
 * React-element-shaped crosses the context.
 *
 * The default context is a no-op so pages rendered outside the provider
 * (mobile webview screens) can register safely.
 */
const NavbarActionsContext = createContext({
  actions: [],
  setActions: () => {},
  trayHidden: false,
  setTrayHidden: () => {},
  pageCrumb: null,
  setPageCrumb: () => {},
  headerSlot: null,
  setHeaderSlot: () => {},
});

export function NavbarActionsProvider({ children }) {
  const [actions, setActions] = useState([]);
  // Classic shell only: a page panel that takes over the tray's corner
  // (Forecast V3's docked AI panel) slides the tray off-screen while open.
  // The inset header ignores this.
  const [trayHidden, setTrayHidden] = useState(false);
  // Inset shell: a page with its own in-page sections (Forecasting's
  // Workspace / Versions / Compare…) appends one crumb to the breadcrumb and
  // the header renders it as the page switcher, so the page's own bar is left
  // for working controls. { title, options: [{ key, title }], onSelect(key) }.
  const [pageCrumb, setPageCrumb] = useState(null);
  // Inset shell: a DOM node beside the breadcrumb that a page may portal its
  // context pickers into (Forecasting's property + version), so long names
  // get the header's spare width instead of crowding the page's own bar.
  const [headerSlot, setHeaderSlot] = useState(null);
  const value = useMemo(
    () => ({ actions, setActions, trayHidden, setTrayHidden, pageCrumb, setPageCrumb, headerSlot, setHeaderSlot }),
    [actions, trayHidden, pageCrumb, headerSlot]
  );
  return <NavbarActionsContext.Provider value={value}>{children}</NavbarActionsContext.Provider>;
}

/** Classic tray: whether the mounted page has claimed the tray's corner. */
export function useNavbarTrayHidden() {
  return useContext(NavbarActionsContext).trayHidden;
}

/** Page side: slide the classic tray out while `hidden`. Restored on unmount. */
export function useHideTray(hidden) {
  const { setTrayHidden } = useContext(NavbarActionsContext);
  useEffect(() => {
    setTrayHidden(!!hidden);
  }, [hidden, setTrayHidden]);
  useEffect(() => () => setTrayHidden(false), [setTrayHidden]);
}

/** Header side: the currently registered page actions. */
export function useNavbarActionSlot() {
  return useContext(NavbarActionsContext).actions;
}

/**
 * Page side: register `actions` while mounted, clear on unmount.
 *
 * Handlers are read through a ref at click time, so callers may pass inline
 * closures without re-registering every render — the header only re-renders
 * when the visible shape (keys, labels, disabled/spinning/primary flags)
 * actually changes.
 */
export function useNavbarActions(actions) {
  const { setActions } = useContext(NavbarActionsContext);
  const latest = useRef(actions);
  latest.current = actions;

  const signature = (actions || [])
    .map((a) => [a.key, a.label, a.text || '', Number(a.badge) || 0, !!a.pressed, !!a.disabled, !!a.spinning, !!a.primary].join(':'))
    .join('|');

  useEffect(() => {
    setActions(
      (latest.current || []).map((a, i) => ({
        ...a,
        onClick: (...args) => latest.current?.[i]?.onClick?.(...args),
      }))
    );
  }, [setActions, signature]);

  useEffect(() => () => setActions([]), [setActions]);
}

export default NavbarActionsContext;

/** Header side: the page-registered breadcrumb switcher, if any. */
export function usePageCrumbSlot() {
  return useContext(NavbarActionsContext).pageCrumb;
}

/**
 * Page side: append `crumb` to the shell breadcrumb while mounted (pass null
 * to register nothing). `onSelect` is read through a ref at click time so an
 * inline closure never re-registers; the header re-renders only when the
 * visible shape changes.
 */
export function usePageCrumb(crumb) {
  const { setPageCrumb } = useContext(NavbarActionsContext);
  const latest = useRef(crumb);
  latest.current = crumb;

  // `back` (optional): { label, title?, onClick, onDismiss? } — a "return to
  // where you came from" chip the header shows beside the page switcher.
  const signature = crumb
    ? [crumb.title, crumb.back?.label || '', ...(crumb.options || []).map((o) => `${o.key}:${o.title}`)].join('|')
    : '';

  useEffect(() => {
    if (!latest.current) {
      setPageCrumb(null);
      return;
    }
    setPageCrumb({
      title: latest.current.title,
      options: latest.current.options || [],
      onSelect: (key) => latest.current?.onSelect?.(key),
      back: latest.current.back
        ? {
          label: latest.current.back.label,
          title: latest.current.back.title,
          onClick: () => latest.current?.back?.onClick?.(),
          onDismiss: () => latest.current?.back?.onDismiss?.(),
        }
        : null,
    });
  }, [setPageCrumb, signature]);

  useEffect(() => () => setPageCrumb(null), [setPageCrumb]);
}

/** Header side: `ref` this onto the element pages may portal context pickers into. */
export function usePageHeaderSlotTarget() {
  return useContext(NavbarActionsContext).setHeaderSlot;
}

/** Page side: the header's picker slot node, or null outside the inset shell. */
export function usePageHeaderSlot() {
  return useContext(NavbarActionsContext).headerSlot;
}
